import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCopy, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import ProcessCardActionsMenu from "./ProcessCardActionsMenu";
import "./process-card-queue.css";

const normalizeText = (value) => String(value ?? "").trim();

const formatDate = (value) => {
  const raw = normalizeText(value);
  if (!raw || raw === "-") return "";

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    return match ? `${match[1]}/${match[2]}/${match[3]}` : raw;
  }

  return parsed.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const safeJoin = (values, separator = " · ") =>
  values.map(normalizeText).filter(Boolean).join(separator);

const resolvePriorityTone = (priority) => {
  const normalized = normalizeText(priority).toUpperCase();
  if (normalized === "CRITICA") return "critical";
  if (normalized === "ALTA") return "high";
  if (normalized === "MEDIA") return "medium";
  if (normalized === "BAJA") return "low";
  return "neutral";
};

const normalizeItem = (item = {}) => ({
  id: Number(item?.id || item?.Id || 0),
  commerceName: normalizeText(item?.comercio || item?.Comercio) || "Sin comercio",
  cuit: normalizeText(item?.cuit || item?.Cuit),
  terminal: normalizeText(item?.numeroTerminal || item?.NumeroTerminal),
  status: normalizeText(item?.estadoLabel || item?.EstadoLabel || item?.estadoActual || item?.EstadoActual) || "Sin estado",
  priority: normalizeText(item?.prioridad || item?.Prioridad),
  responsibleName: normalizeText(item?.responsable || item?.Responsable),
  nextAction: normalizeText(item?.accionPermitida || item?.AccionPermitida),
  nextDate: normalizeText(item?.fechaProximaAccion || item?.FechaProximaAccion),
  attempt: normalizeText(item?.intentoActual || item?.IntentoActual),
  channel: normalizeText(item?.canalPermitido || item?.CanalPermitido),
  origin: normalizeText(item?.procesoOrigenLabel || item?.ProcesoOrigenLabel || item?.procesoOrigenCodigo || item?.ProcesoOrigenCodigo),
  suggestedMessage: normalizeText(item?.mensajeSugerido || item?.MensajeSugerido),
  canManage: item?.puedeGestionar !== false,
  canTake: Boolean(item?.puedeTomar),
  canAssign: Boolean(item?.puedeAsignar),
  canRelease: Boolean(item?.puedeLiberar),
  readOnly: Boolean(item?.esSoloLectura),
  raw: item,
});

export default function ProcessCardQueueSection({
  rows = [],
  loading = false,
  emptyMessage,
  detailLabel = "Detalle",
  searchPlaceholder = "Buscar por comercio, CUIT, terminal...",
  onManage,
  onTake,
  onAssign,
  onRelease,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [search, setSearch] = useState("");
  const normalizedRows = useMemo(() => rows.map((item) => normalizeItem(item)), [rows]);
  const filteredRows = useMemo(() => {
    const needle = normalizeText(search).toLowerCase();
    if (!needle) return normalizedRows;

    return normalizedRows.filter((item) =>
      [
        item.commerceName,
        item.cuit,
        item.terminal,
        item.status,
        item.priority,
        item.responsibleName,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle))
    );
  }, [normalizedRows, search]);

  useEffect(() => {
    if (!filteredRows.length) {
      setExpandedId(null);
      return;
    }

    setExpandedId((current) => (filteredRows.some((item) => item.id === current) ? current : filteredRows[0].id));
  }, [filteredRows]);

  const handleCopyMessage = async (message, itemId) => {
    try {
      await navigator.clipboard.writeText(message || "");
      setCopiedId(itemId);
      window.setTimeout(() => setCopiedId(null), 1600);
    } catch {
      Swal.fire(detailLabel, "No se pudo copiar el mensaje sugerido.", "error");
    }
  };

  if (loading) {
    return (
      <div className="py-4 text-center">
        Cargando {detailLabel.toLowerCase()}...
      </div>
    );
  }

  if (!normalizedRows.length) {
    return (
      <>
        <div className="process-card-queue__search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input
            type="search"
            className="form-control"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={`Buscar en ${detailLabel.toLowerCase()}`}
          />
        </div>
        <Alert variant="secondary" className="mb-0">
          {emptyMessage}
        </Alert>
      </>
    );
  }

  return (
    <>
      <div className="process-card-queue__search">
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          type="search"
          className="form-control"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={`Buscar en ${detailLabel.toLowerCase()}`}
        />
      </div>

      {!filteredRows.length ? (
        <Alert variant="secondary" className="mb-0">
          No se encontraron resultados para la búsqueda actual.
        </Alert>
      ) : (
        <div className="process-card-queue">
          {filteredRows.map((item) => {
        const expanded = expandedId === item.id;
        const secondaryLine = safeJoin([
          item.cuit ? `CUIT: ${item.cuit}` : "",
          item.terminal ? `Terminal: ${item.terminal}` : "",
        ]);

            return (
              <article key={item.id} className={`process-card-item ${expanded ? "is-expanded" : ""}`}>
                <div className="process-card-item__summary">
                  <button
                    type="button"
                    className="process-card-item__identity"
                    onClick={() => setExpandedId((current) => (current === item.id ? null : item.id))}
                    aria-expanded={expanded}
                  >
                    <div className="process-card-item__main">
                      <strong className="process-card-item__commerce">{item.commerceName}</strong>
                      {secondaryLine ? <div className="process-card-item__meta">{secondaryLine}</div> : null}
                    </div>
                  </button>

                  <div className="process-card-item__controls" onClick={(event) => event.stopPropagation()}>
                    <div className="process-card-item__badges">
                      <span className="process-card-item__pill">{item.status}</span>
                      {item.priority ? <span className={`process-card-item__priority is-${resolvePriorityTone(item.priority)}`}>{item.priority}</span> : null}
                    </div>

                    <ProcessCardActionsMenu
                      item={item}
                      onManage={onManage}
                      onTake={onTake}
                      onAssign={onAssign}
                      onRelease={onRelease}
                      onCopyMessage={(message) => handleCopyMessage(message, item.id)}
                    />

                    <button
                  type="button"
                  className="process-card-item__toggle"
                  onClick={() => setExpandedId((current) => (current === item.id ? null : item.id))}
                  aria-label={expanded ? `Contraer ${detailLabel.toLowerCase()}` : `Expandir ${detailLabel.toLowerCase()}`}
                  aria-expanded={expanded}
                >
                  <FontAwesomeIcon icon={faChevronDown} className={expanded ? "is-open" : ""} />
                    </button>
                  </div>
                </div>

                {expanded ? (
                  <div className="process-card-item__detail">
                    <div className="process-card-item__detail-column">
                      <h4>{detailLabel}</h4>
                      <div className="process-card-item__detail-lines">
                        {item.nextAction ? <p><b>Próxima acción:</b> {item.nextAction}</p> : null}
                        {item.nextDate ? <p><b>Fecha próxima:</b> {formatDate(item.nextDate) || item.nextDate}</p> : null}
                        {item.responsibleName ? <p><b>Responsable:</b> {item.responsibleName}</p> : null}
                        {item.origin ? <p><b>Origen:</b> {item.origin}</p> : null}
                        {item.channel ? <p><b>Canal:</b> {item.channel}</p> : null}
                        {item.attempt ? <p><b>Intento:</b> {item.attempt}</p> : null}
                      </div>
                    </div>

                    <div className="process-card-item__detail-column is-message">
                      <div className="process-card-item__message-label">Mensaje sugerido</div>
                      {item.suggestedMessage ? (
                        <>
                          <p className="process-card-item__message-text">{item.suggestedMessage}</p>
                          <Button
                            type="button"
                            variant="link"
                            className="process-card-item__copy"
                            onClick={() => handleCopyMessage(item.suggestedMessage, item.id)}
                          >
                            <FontAwesomeIcon icon={faCopy} />
                            <span>{copiedId === item.id ? "Mensaje copiado" : "Copiar"}</span>
                          </Button>
                        </>
                      ) : (
                        <p className="process-card-item__message-empty">Sin mensaje sugerido disponible.</p>
                      )}
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </>
  );
}
